import { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CurrentTermContext } from '@BUP/contexts/CurrentTermContext'
import { useGetAccessSociogramTeachers } from '@BUP/hooks/Sociogram/teachersData/useGetAccessSociogramTeachers'
import ShowTeacherResultsButton from './show-teacher-results-button.jsx'
import styles from './sociogram-table.module.scss'
import { hasNonEmptyArray, hasNonEmptyObject } from '@BUP/utils/table-validations.js'

export const useTeacherResultsColumn = ({ selectedEvent }) => {
  const i18n = useTranslation()
  const term = useContext(CurrentTermContext)

  const {
    dataSociogramTeachers,
    isLoadingSociogramTeachers
  } = useGetAccessSociogramTeachers(term?.id)

  const teacherAccessByCourseId = useMemo(() => {
    const map = new Map()

    dataSociogramTeachers?.teachers?.forEach((teacher) => {
      teacher.courses?.forEach((course) => {
        const teacherEventAccess = teacher.eventAccess?.find(
          (eventAccess) => eventAccess.eventId === selectedEvent?.id
        )

        map.set(course.id, {
          teacherData: teacher,
          teacherEventAccess
        })
      })
    })

    return map
  }, [dataSociogramTeachers, selectedEvent])

  const teacherResultsColumn = useMemo(() => ({
    Header: i18n.t('pages.data.sociogram.activeSociogramEce.table.resultsMainTeacherHeader'),
    accessor: 'teacherEventAccess',
    disableSortBy: true,
    headerClassName: styles.center_header,
    Cell: ({ row }) => {
      const survey = row?.original

      if (!survey) {
        return (
          <span className={styles.actions_cell_text}>
            {i18n.t('pages.data.sociogram.activeSociogramEce.noSurveyMessage')}
          </span>
        )
      }

      const surveyCourseId = survey.courses?.[0]?.id
      const teacherAccessData = teacherAccessByCourseId.get(surveyCourseId)

      const teacherData = teacherAccessData?.teacherData
      const teacherEventAccess = teacherAccessData?.teacherEventAccess

      const rowHasTeacherData = hasNonEmptyObject(teacherEventAccess)
      const rowHasResults = hasNonEmptyArray(survey.results)

      if (!rowHasTeacherData || !rowHasResults) return null

      return (
        <ShowTeacherResultsButton
          survey={{
            ...survey,
            teacherData,
            teacherEventAccess
          }}
          loading={isLoadingSociogramTeachers}
        />
      )
    }
  }), [i18n, isLoadingSociogramTeachers, teacherAccessByCourseId])

  return {
    teacherAccessByCourseId,
    teacherResultsColumn,
    isLoadingSociogramTeachers
  }
}
